import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { AddToCartStatus } from '@components/Button/AddToCart';
import { BigCommerceCheckoutButton } from '@components/Button/BigCommerceCheckoutButton';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useAddItems } from '@hooks/cart/useAddItems';
import * as Tabs from '@radix-ui/react-tabs';
import Head from 'next/head';
import { type ReactElement, type ReactNode, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, ButtonType } from '../components/Button';
import { Container } from '../components/Container';
import { Input, Textarea } from '../components/FormComponents';

export interface GiftCertificateForm {
  name: string;
  email: string;
  recipient: string;
  recipientEmail: string;
  amount: number;
  message: string;
  terms: boolean;
  theme: string;
}

function PurchaseCertificate() {
  const { setQuickCartIsOpen, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [disabled, setDisabled] = useState(false);
  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      email: '',
      recipient: '',
      recipientEmail: '',
      amount: 0,
      message: '',
      terms: false,
      theme: '',
    },
  });

  const trimDecimals = () => {
    const trimmed = Number(rhfMethods.getValues('amount')).toFixed(2);
    rhfMethods.setValue('amount', trimmed, { shouldValidate: false });
  };

  const handleSubmit = async (e: GiftCertificateForm) => {
    try {
      setAddingToCartStatus(AddToCartStatus.Adding);
      setError(false);
      setDisabled(true);
      const cartResponse = await addItem({
        giftCertificate: {
          name: 'Gift certificate',
          theme: e.theme,
          amount: Number(e.amount),
          message: e.message,
          sender: {
            name: e.name,
            email: e.email,
          },
          recipient: {
            name: e.recipient,
            email: e.recipientEmail,
          },
        },
      });

      if (cartResponse.error !== null) {
        setError(true);
        if (cartResponse.error) {
          setAddingToCartStatus(AddToCartStatus.Failed);
          setError(true);
          setErrorMessage(
            <Paragraph>Error adding the Gift Certificate to cart</Paragraph>
          );
        } else {
          throw new Error('Unexpected error');
        }
      } else {
        // success
        setAddingToCartStatus(AddToCartStatus.Added);
        rhfMethods.reset();
        setQuickCartIsOpen(true); // By placing this here, instead of with the above state it renders once the item is in cart
      }
    } catch (err) {
      setError(true);
      setDisabled(false);
      setAddingToCartStatus(AddToCartStatus.Failed);
      setErrorMessage(
        <Paragraph>
          We couldn't add that to your cart. If you continue to have issues,
          please contact customer service.
        </Paragraph>
      );
    } finally {
      setDisabled(false);
    }
  };

  return (
    <>
      {error && (
        <ErrorAlert show={error} setShow={setError}>
          {errorMessage}
        </ErrorAlert>
      )}
      <div>
        <FormProvider {...rhfMethods}>
          <form
            className="flex flex-col gap-4 sm:grid sm:grid-cols-2"
            onSubmit={rhfMethods.handleSubmit(handleSubmit)}
          >
            <Input
              label="Your Name"
              name="name"
              type="text"
              placeholder=""
              required
            />
            <Input label="Your Email" name="email" type="email" required />
            <Input
              label="Recipient's Name"
              name="recipient"
              type="text"
              required
            />
            <Input
              label="Recipient's Email"
              name="recipientEmail"
              type="email"
              required
            />
            <Input
              label="Amount"
              name="amount"
              type="number"
              required
              min={1}
              max={1000}
              onBlur={trimDecimals}
            />
            <Textarea label="Optional Message" name="message" rows={5} />
            <Input
              type="checkbox"
              label="I agree that Gift Certificates are non-refundable"
              name="terms"
              required
            />
            <Input
              type="radio"
              label="Gift Certificate Theme"
              name="theme"
              options={[
                { value: 'Birthday', label: 'Birthday' },
                { value: 'Celebration', label: 'Celebration' },
                { value: 'Christmas', label: 'Christmas' },
                { value: 'General', label: 'General' },
              ]}
              required
            />

            <Button
              type={ButtonType.submit}
              disabled={disabled}
              buttonStyle="primary"
              className="cols-end-auto col-start-[span_2] m-auto w-fit font-primary"
            >
              Add Gift Certificate to Cart
            </Button>
          </form>
        </FormProvider>
      </div>
    </>
  );
}

function RedeemCertificate() {
  return (
    <>
      <h3>Redeem Gift Certificate</h3>
      <Paragraph>
        To redeem a gift certificate at Bulk Nutrients, follow the simple steps
        below.
      </Paragraph>
      <ol className="list-decimal pl-4">
        <li>
          You need your unique gift certificate code, which is part of the gift
          certificate that was emailed to you as an attachment. It will look
          something like Z50-Y6K-COS-402.
        </li>
        <li>
          Browse the store and add items to your cart as you normally would.
        </li>
        <li>
          Click the{' '}
          <span>
            <BigCommerceCheckoutButton
              styledButton={false}
              className="font-semibold text-grey-dark underline 
            decoration-grey-mid transition-all duration-200 ease-in-out
            hover:text-black hover:underline
            hover:decoration-orange hover:decoration-3 hover:underline-offset-[0.1em]"
              location="gift-certificate-redeem"
            />
          </span>{' '}
          link to start the checkout process.
        </li>
        <li>
          Type your gift certificate code into the 'Coupon/Gift Certificate' box
          and press the 'Apply' button.
        </li>
        <li>Complete the checkout process as usual.</li>
      </ol>
    </>
  );
}

function CheckBalance() {
  const [balance, setBalance] = useState<number>();
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [disabled, setDisabled] = useState(false);

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      code: '',
    },
  });

  const handleSubmit = async (e: { code: string }) => {
    try {
      setError(false);
      setDisabled(true);
      setCode(e.code);
      const res = await fetch(`/api/giftcertificate?code=${e.code}`, {
        method: 'GET',
      });

      if (res.status === 200) {
        // success

        const { data: balance } = await res.json();
        setBalance(balance);
      } else {
        // fail
        const { error } = await res.json();
        setError(true);
        setBalance(undefined);
        setErrorMessage(<Paragraph>{error.message.join('. ')}</Paragraph>);
      }
      setDisabled(false);
    } catch (err) {
      setError(true);
      setDisabled(false);
      setBalance(undefined);
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <h3>Check Gift Certificate Balance</h3>
      <Paragraph>
        You can check the balance of a gift certificate by typing the code in to
        the box below.
      </Paragraph>
      {balance && (
        <Paragraph>
          There is <strong>${balance.toFixed(2)}</strong> remaining on {code}.
        </Paragraph>
      )}
      <FormProvider {...rhfMethods}>
        <form
          className="flex max-w-[300px] flex-col items-start gap-4"
          onSubmit={rhfMethods.handleSubmit(handleSubmit)}
        >
          <Input
            label="Gift Certificate Code"
            name="code"
            type="text"
            placeholder=""
            required
          />

          <Button
            type={ButtonType.submit}
            disabled={disabled}
            buttonStyle="primary"
          >
            Check Balance
          </Button>
        </form>
      </FormProvider>
    </>
  );
}

export default function GiftCertificatePage(): ReactElement {
  return (
    <>
      <Head>
        <title>Bulk Nutrients Gift Certificates - Supplement Gift Cards</title>
        <meta
          name="title"
          content="Bulk Nutrients Gift Certificates - Supplement Gift Cards"
        />
        <meta
          name="description"
          content="Buy gift certificates and gift cards for Bulk Nutrients online supplement store in Australia. Redeem gift certificates and gift cards at Bulk Nutrients"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/giftcertificates`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Gift Certificates', path: '/giftcertificates' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <h1>Gift Certificates</h1>
        <Tabs.Root defaultValue="purchase">
          <Tabs.List className="flex justify-center gap-2.5 pb-4">
            <Tabs.Trigger
              className="border-none bg-transparent text-[1.05rem] text-grey-dark hover:underline
              data-[state=active]:underline dark:text-grey-mid"
              value="purchase"
            >
              Purchase Gift Certificate
            </Tabs.Trigger>
            <Tabs.Trigger
              className="border-none bg-transparent text-[1.05rem] text-grey-dark hover:underline 
              data-[state=active]:underline dark:text-grey-mid"
              value="redeem"
            >
              Redeem Gift Certificate
            </Tabs.Trigger>
            <Tabs.Trigger
              className="border-none bg-transparent text-[1.05rem] text-grey-dark hover:underline 
              data-[state=active]:underline dark:text-grey-mid"
              value="balance"
            >
              Check Gift Certificate Balance
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="purchase" asChild>
            <PurchaseCertificate />
          </Tabs.Content>
          <Tabs.Content value="redeem" asChild>
            <RedeemCertificate />
          </Tabs.Content>
          <Tabs.Content value="balance" asChild>
            <CheckBalance />
          </Tabs.Content>
        </Tabs.Root>
      </Container>
    </>
  );
}

GiftCertificatePage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
